import React, { useState } from 'react';
import axios from 'axios';
import { Shield, Key, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TwoFactorVerify({ onSuccess, onCancel }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');

  const verifyCode = async () => {
    if (!code || code.length !== 6) {
      toast.error('Digite o código de 6 dígitos');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/security/2fa/verify`, {
        code
      }, {
        withCredentials: true
      });
      
      toast.success('Login realizado com sucesso!');
      
      if (onSuccess) {
        onSuccess(response.data.user);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido');
    } finally {
      setLoading(false);
    }
  };

  const submitRecoveryCode = async () => {
    if (!recoveryCode) {
      toast.error('Digite o código de recuperação');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/security/2fa/verify-recovery`, {
        recovery_code: recoveryCode.toUpperCase()
      }, {
        withCredentials: true
      });
      
      toast.success('Login realizado com código de recuperação');
      
      if (response.data.warning) {
        toast.warning(response.data.warning);
      }
      
      if (onSuccess) {
        onSuccess(response.data.user);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código de recuperação inválido');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-stone-900/50 rounded-2xl border border-stone-800 p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-amber-500/20 rounded-2xl flex items-center justify-center mb-4">
              <Shield className="w-8 h-8 text-amber-500" />
            </div>
            <h1 className="text-2xl font-bold text-stone-100">Verificação em Dois Fatores</h1>
            <p className="text-stone-400 mt-2">
              {showRecovery 
                ? 'Digite um código de recuperação'
                : 'Digite o código do seu autenticador'
              }
            </p>
          </div>

          {!showRecovery ? (
            /* TOTP Code Input */
            <div className="space-y-6">
              <div>
                <label className="text-sm text-stone-400 block mb-2">
                  Código do Autenticador
                </label>
                <Input
                  type="text"
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-3xl tracking-[0.5em] bg-stone-800 border-stone-700 h-16"
                  maxLength={6}
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && verifyCode()}
                />
              </div>

              <Button
                onClick={verifyCode}
                disabled={loading || code.length !== 6}
                className="w-full bg-amber-500 hover:bg-amber-600 text-stone-900 h-12"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Verificar'
                )}
              </Button>

              <div className="text-center">
                <button
                  onClick={() => setShowRecovery(true)}
                  className="text-sm text-stone-400 hover:text-amber-500 transition-colors"
                >
                  Perdeu acesso ao autenticador? Use um código de recuperação
                </button>
              </div>
            </div>
          ) : (
            /* Recovery Code Input */
            <div className="space-y-6">
              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-stone-300">
                    Códigos de recuperação são de uso único. Após usar este código, 
                    recomendamos reconfigurar o 2FA.
                  </p>
                </div>
              </div>

              <div>
                <label className="text-sm text-stone-400 block mb-2">
                  Código de Recuperação
                </label>
                <Input
                  type="text"
                  placeholder="XXXX-XXXX-XXXX"
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                  className="text-center text-xl tracking-wider bg-stone-800 border-stone-700 h-14 font-mono"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && submitRecoveryCode()}
                />
              </div>

              <Button
                onClick={submitRecoveryCode}
                disabled={loading || !recoveryCode}
                className="w-full bg-amber-500 hover:bg-amber-600 text-stone-900 h-12"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Usar Código de Recuperação'
                )}
              </Button>

              <div className="text-center">
                <button
                  onClick={() => {
                    setShowRecovery(false);
                    setRecoveryCode('');
                  }}
                  className="text-sm text-stone-400 hover:text-amber-500 transition-colors"
                >
                  Voltar para código do autenticador
                </button>
              </div>
            </div>
          )}

          {/* Cancel */}
          <div className="mt-6 text-center">
            <button
              onClick={onCancel}
              className="text-sm text-stone-500 hover:text-stone-400 transition-colors"
            >
              Cancelar e voltar ao login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
